
import React, { useState } from 'react';
import { useApp } from '../AppContext';
import { Plus, Loader2, ShoppingBag, X, Info } from 'lucide-react';
import { Product } from '../types';

const StoreFront: React.FC = () => {
  const { currentStore, addToCart, isLoading, error, user } = useApp();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="relative">
          <Loader2 className="animate-spin text-indigo-600" size={48} />
          <ShoppingBag className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600" size={20} />
        </div>
        <p className="text-gray-500 font-medium animate-pulse">Carregando catálogo...</p>
      </div>
    );
  }

  if (error || !currentStore) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="bg-red-50 p-4 rounded-full mb-4">
          <ShoppingBag size={48} className="text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Loja não encontrada</h2>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          {error || "O endereço informado parece estar incorreto ou a loja não está disponível no momento."}
        </p>
        
        {/* Só mostra o botão de voltar ao admin se o usuário for o dono (estiver logado) */}
        {user ? (
          <button 
            onClick={() => window.location.hash = '#/admin'}
            className="mt-8 bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-indigo-700 transition-all transform active:scale-95"
          >
            Voltar ao Painel
          </button>
        ) : (
          <p className="mt-8 text-sm text-gray-400">Verifique se o link está correto com o vendedor.</p>
        )}
      </div>
    );
  }

  const products = currentStore.products || [];
  const categories = Array.from(new Set(products.map(p => p.category))).sort();

  const handleOpenDetails = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleCloseDetails = () => {
    setSelectedProduct(null);
  };

  return (
    <div className="max-w-7xl mx-auto pb-20">
      <div className="relative h-48 md:h-80 w-full overflow-hidden mb-8 md:rounded-b-[40px] shadow-xl">
        <img 
          src={currentStore.banner_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200'} 
          alt={currentStore.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
          <div className="flex items-end gap-4">
            <div className="p-1 bg-white rounded-3xl shadow-xl">
              <img 
                src={currentStore.logo_url || 'https://via.placeholder.com/200'} 
                alt="Logo"
                className="w-20 h-20 md:w-32 md:h-32 rounded-2xl object-cover"
              />
            </div>
            <div className="mb-2">
              <h2 className="text-2xl md:text-4xl font-black text-white leading-none mb-1 drop-shadow-md">
                {currentStore.name}
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 space-y-12 mt-4">
        {categories.length === 0 ? (
          <div className="py-24 text-center">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Catálogo Vazio</h3>
            <p className="text-gray-500 mt-1">Esta loja ainda não possui itens cadastrados.</p>
          </div>
        ) : (
          categories.map(category => (
            <section key={category}>
              <div className="flex items-center gap-4 mb-8">
                <h3 className="text-2xl font-black text-gray-900 whitespace-nowrap">
                  {category}
                </h3>
                <div className="h-[2px] w-full bg-gray-100" />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {products
                  .filter(p => p.category === category)
                  .map(product => (
                    <div 
                      key={product.id} 
                      onClick={() => handleOpenDetails(product)}
                      className="group bg-white rounded-[2rem] overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-indigo-100 transition-all duration-300 flex flex-col cursor-pointer"
                    >
                      <div className="relative h-56 overflow-hidden bg-gray-50">
                        <img 
                          src={product.image_url || 'https://via.placeholder.com/400x300?text=Sem+Foto'} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity">
                          <Info size={18} className="text-indigo-600" />
                        </div>
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <h4 className="font-bold text-xl text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                          {product.name}
                        </h4>
                        <p className="text-sm text-gray-500 mt-2 h-10 line-clamp-2 leading-tight flex-1">
                          {product.description || "Sem descrição disponível."}
                        </p>
                        <div className="mt-6 flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-2xl font-black tracking-tight" style={{ color: currentStore.primary_color || '#4f46e5' }}>
                              R$ {Number(product.price).toFixed(2)}
                            </span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest -mt-1">
                              por {product.unit || 'un'}
                            </span>
                          </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(product);
                            }}
                            className="flex items-center gap-2 text-white font-bold py-3 px-5 rounded-2xl transition-all transform active:scale-95 shadow-lg"
                            style={{ backgroundColor: currentStore.primary_color || '#4f46e5' }}
                          >
                            <Plus size={20} />
                            Adicionar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )
        ))}
      </div>

      {/* Modal de Detalhes do Produto */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in">
          <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in slide-in-from-bottom-8">
            <div className="relative">
              <button 
                onClick={handleCloseDetails}
                className="absolute top-6 right-6 z-10 p-3 bg-white/90 backdrop-blur-md rounded-full text-gray-900 shadow-xl hover:bg-white transition-all transform hover:rotate-90 active:scale-90"
              >
                <X size={24} />
              </button>
              <div className="h-64 sm:h-80 w-full bg-gray-100">
                <img 
                  src={selectedProduct.image_url || 'https://via.placeholder.com/800x600?text=Sem+Foto'} 
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="p-8 sm:p-10">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
                <div>
                  <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest rounded-full mb-2">
                    {selectedProduct.category}
                  </span>
                  <h2 className="text-3xl font-black text-gray-900 tracking-tight leading-tight">
                    {selectedProduct.name}
                  </h2>
                </div>
                <div className="flex flex-col items-start sm:items-end">
                  <span className="text-3xl font-black tracking-tight" style={{ color: currentStore.primary_color || '#4f46e5' }}>
                    R$ {Number(selectedProduct.price).toFixed(2)}
                  </span>
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Preço por {selectedProduct.unit || 'unidade'}
                  </span>
                </div>
              </div>

              <div className="space-y-4 mb-10">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Descrição Completa</h4>
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 max-h-48 overflow-y-auto">
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {selectedProduct.description || "Nenhuma descrição adicional informada para este produto."}
                  </p>
                </div>
              </div>

              <button 
                onClick={() => {
                  addToCart(selectedProduct);
                  handleCloseDetails();
                }}
                className="w-full flex items-center justify-center gap-3 text-white font-black py-5 rounded-[2rem] shadow-xl transition-all transform active:scale-95"
                style={{ backgroundColor: currentStore.primary_color || '#4f46e5' }}
              >
                <Plus size={24} />
                Adicionar ao Carrinho
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreFront;
